/**
 * TrustedRitualists Component
 * Horizontal scrollable list of trusted ritual creators using RitualistAvatar cards.
 */

import { ArrowRight } from "lucide-react"
import { Carousel } from "@/components/carousel/Carousel"
import { RitualistAvatar } from "@/features/connect/components/RitualistAvatar"
import type { HighlightedRitualist } from "@/features/connect/types/connect.types"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"

interface TrustedRitualistsProps {
  ritualists: HighlightedRitualist[]
  onShowMore?: () => void
  onFollow?: (id: string) => void
  onUnfollow?: (id: string) => void
}

export function TrustedRitualists({
  ritualists,
  onShowMore,
  onFollow,
  onUnfollow,
}: TrustedRitualistsProps) {
  const isMobile = useIsMobile()
  const { t } = useTranslation("learnPractice")

  return (
    <section className="pb-10 max-w-360 mx-auto md:px-17.5">
      <div className="flex items-center justify-between mb-2 px-5 sm:px-0">
        <h2 className="section-heading text-2xl text-primary uppercase leading-7.5 font-semibold opacity-90">
          {t("sections.trustedRitualists")}
        </h2>
        {onShowMore && !isMobile && (
          <button
            type="button"
            onClick={onShowMore}
            className="text-sm text-primary font-semibold transition-colors flex items-center gap-1.25 shrink-0 cursor-pointer"
          >
            {t("actions.showMore")}
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
      <p className="text-primary text-sm leading-6.25 opacity-90 mb-5 px-5 sm:px-0">
        {t("sections.trustedRitualistsDescription")}
      </p>

      <Carousel
        data={ritualists}
        renderItem={ritualist => (
          <RitualistAvatar ritualist={ritualist} onFollow={onFollow} onUnfollow={onUnfollow} />
        )}
        itemWidth="300px"
        contentClassName="gap-5 touch-pan-x"
        opts={{ align: "start", loop: false }}
      />
      {onShowMore && isMobile && (
        <button
          type="button"
          onClick={onShowMore}
          className="text-sm text-primary font-semibold transition-colors mt-5 flex items-center gap-1.25 shrink-0 cursor-pointer px-5"
        >
          {t("actions.showMore")}
          <ArrowRight className="w-5 h-5" />
        </button>
      )}
    </section>
  )
}
